import React, { useEffect } from 'react';
import { FunctionComponent } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { boardSelectors } from '../../../../../app/resource/boards/board/board.selectors';
import { boardListSelectors } from '../../../../../app/resource/boards/boardList/boardList.selectors';
import { useRequiredValidation } from '../../../../../utils/form/validation.utils';
import { useSelect, useSelectWithParams } from '../../../../../utils/selector.utils';
import { IAddAgendaFormProps } from './types';
import { FormTextInput } from '../../../../pages/common/inputs/TextInput';
import { SelectInput } from '../../../../pages/common/inputs/SelectInput';
import { FormCSTextInput } from '../../../../pages/common/inputs/CustomSizeTextInput';
import { IUpdateAgendaItemForm } from '../AddAgendaForm/types';
import { InfoIconPopup } from '../../../../pages/common/InfoIconPopup';
import {
  TODO,
  ROCK_REVIEW,
  KPI,
  CONCLUSION,
  FREE_ACTIVITY,
  ACCOUNTABILITY_LIST,
  ACCOUNTABILITY_BOARD,
  EXTERNAL_URL,
} from '../../../../../app/resource/agenda-item/agenda-item.types';

const connectOptions = [
  { value: FREE_ACTIVITY, label: 'Free activity' },
  { value: KPI, label: 'Kpi Review' },
  { value: ROCK_REVIEW, label: 'Goals Review' },
  { value: TODO, label: 'Action Items Review' },
  { value: ACCOUNTABILITY_BOARD, label: 'Accountability Board' },
  { value: ACCOUNTABILITY_LIST, label: 'Accountability List' },
  { value: EXTERNAL_URL, label: 'External URL' },
  { value: CONCLUSION, label: 'Meeting Scores' },
];

export const AddAgendaForm: FunctionComponent<IAddAgendaFormProps> = ({
  boardIds,
  onCreateSubmit,
  onUpdateSubmit,
  editableItem,
}) => {
  // only simple text inputs default values are set here
  const formProps = useForm({
    defaultValues: {
      title: editableItem?.title,
      description: editableItem?.description,
      embedUrl: editableItem?.embedUrl,
      duration: editableItem?.duration,
      keyName: editableItem?.keyName,
    },
  });

  const reset = formProps.reset;

  const requiredValidation = useRequiredValidation();
  const boardOptions = useSelectWithParams(boardSelectors.selectResourcesById, boardIds).map((board) => ({
    value: board?.id,
    label: board?.name,
  }));

  useEffect(() => {
    if (editableItem) {
      reset({
        title: editableItem?.title,
        description: editableItem?.description,
        embedUrl: editableItem?.embedUrl,
        duration: editableItem?.duration,
        keyName: editableItem?.keyName,
      });
    }
  }, [editableItem, reset]);

  let defaultFocusConnectTuple: { value: string; label: string } | undefined = undefined;
  if (editableItem) {
    switch (editableItem.keyName) {
      case ACCOUNTABILITY_BOARD:
        defaultFocusConnectTuple = {
          value: ACCOUNTABILITY_BOARD,
          label: 'Accountability Board',
        };
        break;
      case ACCOUNTABILITY_LIST:
        defaultFocusConnectTuple = {
          value: ACCOUNTABILITY_LIST,
          label: 'Accountability List',
        };
        break;
      case EXTERNAL_URL:
        defaultFocusConnectTuple = { value: EXTERNAL_URL, label: 'External URL' };
        break;
      case FREE_ACTIVITY:
        defaultFocusConnectTuple = { value: FREE_ACTIVITY, label: 'Free activity' };
        break;
      case KPI:
        defaultFocusConnectTuple = { value: KPI, label: 'Kpi Review' };
        break;
      case ROCK_REVIEW:
        defaultFocusConnectTuple = { value: ROCK_REVIEW, label: 'Goals Review' };
        break;
      case TODO:
        defaultFocusConnectTuple = { value: TODO, label: 'Action Items Review' };
        break;
      case CONCLUSION:
        defaultFocusConnectTuple = { value: CONCLUSION, label: 'Meeting Scores' };
        break;
    }
  }

  const focusConnectTuple = formProps.watch('control', defaultFocusConnectTuple);

  const defaultBoard = boardOptions.find((board) => board.value === editableItem?.boardId);

  const focusBoardTuple: { value: string } = formProps.watch('board');

  const boardLists = useSelect(boardListSelectors.selectResourceList);
  let filteredBoardLists = boardLists;

  if (focusBoardTuple) {
    filteredBoardLists = boardLists.filter((boardList) => boardList.boardId === focusBoardTuple.value);
  } else {
    filteredBoardLists = boardLists.filter((boardList) => boardList.boardId === editableItem?.boardId);
  }

  const boardListOptions = filteredBoardLists.map((boardList) => ({ value: boardList.id, label: boardList.name }));
  const defaultBoardList = boardListOptions.find((boardList) => boardList.value === editableItem?.boardListId);

  const assignAgendaIdAndSubmit = (formData: any) => {
    let data: IUpdateAgendaItemForm = { ...formData };
    if (editableItem) {
      data = {
        ...data,
        index: editableItem.index,
        checked: editableItem.checked ?? true,
        board: formData.board ?? '',
        boardList: formData.boardList ?? '',
        keyName: formData.control.value,
      };
      onUpdateSubmit(data);
    } else {
      data = { ...data, keyName: formData.control.value };
      onCreateSubmit(data);
    }
  };

  return (
    <FormProvider {...formProps}>
      <form
        id="addAgenda-form"
        className="topic-form"
        onSubmit={formProps.handleSubmit((formData) => assignAgendaIdAndSubmit(formData))}
        method="post"
      >
        <div className="field-container">
          <FormTextInput
            id="title"
            name="title"
            type="text"
            label="Title"
            placeholder="Enter agenda item title"
            validation={requiredValidation}
          />
        </div>
        <div className="field-container">
          <SelectInput
            id="control"
            name="control"
            label="Connect to"
            validation={requiredValidation}
            options={connectOptions}
            defaultOptions={defaultFocusConnectTuple}
            isSingle
          />
        </div>
        {focusConnectTuple &&
          (focusConnectTuple.value === ACCOUNTABILITY_BOARD || focusConnectTuple.value === ACCOUNTABILITY_LIST) && (
            <div className="field-container">
              <SelectInput
                id="board"
                name="board"
                label="Board"
                validation={requiredValidation}
                options={boardOptions}
                defaultOptions={
                  defaultBoard && defaultBoard.value !== undefined && defaultBoard.label !== undefined
                    ? { value: defaultBoard.value, label: defaultBoard.label }
                    : { value: '', label: '' }
                }
                isSingle
              />
            </div>
          )}
        {focusConnectTuple && focusConnectTuple.value === ACCOUNTABILITY_LIST && (
          <div className="field-container">
            <SelectInput
              id="boardList"
              name="boardList"
              label="Board List"
              validation={requiredValidation}
              options={boardListOptions}
              defaultOptions={defaultBoardList}
              isSingle
            />
          </div>
        )}
        {focusConnectTuple && focusConnectTuple.value === EXTERNAL_URL && (
          <div className="field-container">
            <FormTextInput
              id="embedUrl"
              name="embedUrl"
              type="text"
              label="Embed URL or code snippet"
              placeholder="Enter"
              validation={requiredValidation}
              rightIcon={
                <InfoIconPopup size={15}>
                  <>
                    To embed your Office 365 document:
                    <br />
                    1. Open your document and navigate to <strong>File</strong> &gt; <strong>Share</strong> &gt;{' '}
                    <strong>Embed this workbook</strong>.
                    <br />
                    2. Copy the code snippet starting with &lt;iframe... from the bottom of the &ldquo;Embed code&rdquo;
                    section.
                    <br />
                    3. Paste the copied snippet into the &ldquo;Embed URL&rdquo; field.{' '}
                  </>
                </InfoIconPopup>
              }
            />
          </div>
        )}
        <div className="field-container">
          <FormTextInput
            id="description"
            name="description"
            type="text"
            label="Description"
            placeholder="Description"
          />
        </div>
        <div className="field-container">
          <FormCSTextInput
            id="duration"
            name="duration"
            type="text"
            size="mw"
            label="Duration"
            placeholder="Minutes"
            validation={requiredValidation}
          />
        </div>
      </form>
    </FormProvider>
  );
};
